import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Stethoscope, CheckCircle2, Shield, FileCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Behandlungspflege() {
  return (
    <>
      <Helmet>
        <title>Behandlungspflege Itzehoe | Medizinische Pflege zuhause | Phönix Pflegeteam</title>
        <meta name="description" content="Behandlungspflege Itzehoe ✓ Medikamente ✓ Wundversorgung ✓ Injektionen ✓ Blutdruck ✓ 55€/h ✓ 100% Kassenübernahme auf Verordnung ✓ Ärztlich verordnet" />
        <link rel="canonical" href="https://pflegeteam-itzehoe.de/behandlungspflege" />
      </Helmet>

      <main id="main-content">
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
                <Stethoscope className="w-4 h-4 text-[#1A52D8]" />
                <span className="text-sm font-medium text-[#1A52D8]">Medizinische Pflege</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Behandlungspflege Itzehoe - Wenn der Arzt es verordnet
              </h1>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#1A52D8]/20 mb-8 direct-answer">
                <p className="text-lg text-[#475569] leading-relaxed mb-4">
                  <strong className="text-[#1A52D8]">Behandlungspflege umfasst:</strong> Alle medizinischen Maßnahmen, die Ihr Arzt verordnet - Medikamentengabe, Wundversorgung, Injektionen, Blutdruckmessung, Kompressionsstrümpfe und mehr.
                </p>
                <p className="text-lg text-[#475569] leading-relaxed">
                  <strong>Kosten:</strong> 55€/Stunde. <strong>Wichtig:</strong> Mit ärztlicher Verordnung übernimmt die Krankenkasse 100% - auch ohne Pflegegrad!
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl mb-8">
                <div className="flex items-start gap-3">
                  <FileCheck className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-[#1e293b] mb-2">Wichtig zu wissen:</h3>
                    <p className="text-[#475569]">Behandlungspflege ist KEINE Pflegeleistung, sondern eine Krankenkassenleistung. Das bedeutet: Sie brauchen KEINEN Pflegegrad! Eine Verordnung vom Hausarzt reicht. Wir rechnen direkt mit Ihrer Krankenkasse ab.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Typische Behandlungspflege-Situationen in Itzehoe
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <h4 className="font-bold text-[#1e293b] mb-3">Nach Krankenhaus-Entlassung</h4>
                <p className="text-[#64748b] mb-3">
                  Sie kommen aus dem Klinikum Itzehoe nach Hause. Wunde muss täglich versorgt werden, Medikamente sind kompliziert, Drainagen müssen kontrolliert werden. Ihr Arzt verordnet Behandlungspflege - wir übernehmen alles. Sie können zuhause gesund werden statt im Heim.
                </p>
                <p className="text-sm text-[#23D3D3] font-semibold">→ 100% Kostenübernahme durch Krankenkasse</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <h4 className="font-bold text-[#1e293b] mb-3">Chronische Wunden (Dekubitus, Ulcus)</h4>
                <p className="text-[#64748b] mb-3">
                  Wunden, die nicht heilen wollen, brauchen professionelle tägliche Versorgung. Unsere examinierten Pflegekräfte dokumentieren genau, arbeiten nach ärztlicher Anweisung und melden Verschlechterungen sofort. In Itzehoe betreuen wir mehrere Patienten mit chronischen Wunden - langfristig und erfolgreich.
                </p>
                <p className="text-sm text-[#23D3D3] font-semibold">→ So lange nötig, so lange verordnet</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                <h4 className="font-bold text-[#1e293b] mb-3">Diabetes-Einstellung & Insulin</h4>
                <p className="text-[#64748b] mb-3">
                  Blutzucker messen, Insulin spritzen, Fußpflege bei Diabetikern - das machen wir täglich bei vielen Patienten in Itzehoe. Gerade ältere Menschen trauen sich das Spritzen oft nicht selbst zu. Mit ärztlicher Verordnung übernehmen wir das - kostenlos für Sie.
                </p>
                <p className="text-sm text-[#23D3D3] font-semibold">→ Täglich möglich, auch mehrmals</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              So bekommen Sie Behandlungspflege
            </h2>

            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1A52D8] text-white flex items-center justify-center font-bold">1</div>
                <div>
                  <h4 className="font-bold text-[#1e293b] mb-1">Arzt verordnet Behandlungspflege</h4>
                  <p className="text-[#64748b]">Ihr Hausarzt stellt ein Rezept aus (HKP-Verordnung). Dort steht genau, was wir machen sollen und wie oft.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1A52D8] text-white flex items-center justify-center font-bold">2</div>
                <div>
                  <h4 className="font-bold text-[#1e293b] mb-1">Sie rufen uns an</h4>
                  <p className="text-[#64748b]">Wir besprechen, was benötigt wird und planen die ersten Termine - oft noch am selben oder nächsten Tag.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1A52D8] text-white flex items-center justify-center font-bold">3</div>
                <div>
                  <h4 className="font-bold text-[#1e293b] mb-1">Wir rechnen mit der Kasse ab</h4>
                  <p className="text-[#64748b]">Sie zahlen nichts. Wir reichen die Verordnung bei Ihrer Krankenkasse ein und bekommen das Geld direkt. Null Aufwand für Sie.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white">
          <div className="container-custom max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Brauchen Sie Behandlungspflege in Itzehoe?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Sprechen Sie mit Ihrem Arzt über eine Verordnung. Wir kümmern uns um den Rest - auch kurzfristig.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt beraten lassen: 0151 46173685
              </Button>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
